import { type Libraries, PriceOverrideStatus } from "@classes-service-types/index";
import { convertCost } from "@/components/program-manager/sessions/utils";
import type {
  PriceOption,
  PriceOverride,
} from "@/components/StudentFulfillment/components/modals/ClassesFeeFulfillmentModal/types";
import {
  LIBRARY_IDS,
  OPTIONS_PRICE_OVERRIDE,
  ORDER_PRODUCT_STATUS,
  REASON_IDS_THAT_AVOID_OVERRIDES,
  REVERTED_OVERRIDE_REASON_ID,
} from "@/constants";
import { centsToDollars } from "@/mixins/helpers";
import type { currentPriceConfig } from "./types";

/**
 * Function that determines what we will show as "Current" Price for the student; the latest product price in the database or the line item price in the order.
 * @param {Boolean} hasPendingPriceOverride - Flag, whether or not we have a pending price override.
 * @param {Number} productLatestPriceInCents - We found a price newer/different from what's currently in the order.
 * @param {Number} lineItemPriceInCents - The total price of the line item currently in the order.
 *
 * @returns {currentPriceConfig} - An object that controls if 'Current Price' shows when selecting 'Keep Order Price' and the price to display, properly formatted.
 */
export const showCurrentPrice = (
  hasPendingPriceOverride: boolean = false,
  productLatestPriceInCents: number = 0,
  lineItemPriceInCents: number = 0,
): currentPriceConfig => {
  if (hasPendingPriceOverride && Number.isFinite(productLatestPriceInCents)) {
    return {
      display: true,
      currentPriceFormatted: `${convertCost(centsToDollars(productLatestPriceInCents))}`,
    };
  }

  return {
    display: false,
    currentPriceFormatted: `${convertCost(centsToDollars(lineItemPriceInCents))}`,
  };
};

/**
 * Determines if we need to get the latest price of a line item.
 * @param {Object} line_item - The current line item to make validations against
 * @returns {undefined/Boolean} - boolean; wether or not to get latest price. Undefined; might apply latest pricing depending on other conditions in backend
 */
export const findLatestPrice = (line_item: Record<string, any>) => {
  if (line_item?.status === ORDER_PRODUCT_STATUS.unfulfilled) {
    return undefined;
  }

  if (
    line_item?.price_option === OPTIONS_PRICE_OVERRIDE.KEEP_CURRENT.value ||
    line_item?.price_option === OPTIONS_PRICE_OVERRIDE.CUSTOM_PRICE.value
  ) {
    return true;
  } else if (line_item?.price_option === OPTIONS_PRICE_OVERRIDE.KEEP_ORDER.value) {
    return !REASON_IDS_THAT_AVOID_OVERRIDES.includes(line_item?.price_override?.override_reason_id);
  }

  return false;
};

/**
 * Returns price before fulfillment or the current line item price
 * @param {Object} line_item - The object where we will try to pull the price from
 * @returns {int} The price before fulfillment or the line item price
 */
export const getPriceBeforeFulfillment = (line_item) => {
  return line_item?.price_before_fulfillment_in_cents ?? line_item.total_price_in_cents;
};

/**
 * Checks we have the minimum requirements to create a price override
 * @param {Boolean} previous_step_went_ok = If previous request (line item update) went ok, set to true if not needed
 * @param {Object} line_item The line item to check if has price override id or reason id
 * @returns {Boolean} User could create price override
 */
export const canCreatePriceOverride = (previous_step_went_ok = false, line_item) => {
  const user_wants_to_create_price_override =
    !line_item?.price_override.id && !!line_item.price_override.override_reason_id;
  const reason_allows_creation = !REASON_IDS_THAT_AVOID_OVERRIDES.includes(
    line_item.price_override.override_reason_id,
  );
  return previous_step_went_ok && user_wants_to_create_price_override && reason_allows_creation;
};

/**
 * Checks we have the minimum requirements to update a price override
 * @param {Boolean} previous_step_went_ok = If previous request (line item update) went ok, set to true if not needed
 * @param {Object} line_item The line item to check if has price override id or reason id
 * @returns {Boolean} User could create price override
 */
export const canUpdatePriceOverride = (previous_step_went_ok = false, line_item) => {
  const has_override_id = !!line_item?.price_override?.id;
  return previous_step_went_ok && has_override_id;
};

/**
 * @param {Object} option - option of the dropdown (expects label/value properties)
 * @param {Boolean} previously_approved - wether or not the line item was previosuly approved
 * Disables specific dropdown option is line_item was previously approved
 */
export const disableKeepCurrent = (option, previously_approved = false) => {
  return option?.value === OPTIONS_PRICE_OVERRIDE.KEEP_CURRENT.value && previously_approved;
};

/**
 * Returns an object showing if bed is about to be assigned and it's price in cents or dollars
 * @param {Object} line_item The line item to check if it has a special property
 * @returns {Object}
 */
export const lineItemAboutToAssignBed = (line_item) => {
  const addingNewBed = Number.isFinite(line_item.previous_bed_price_in_cents);
  return {
    addingNewBed: addingNewBed,
    price_in_cents: addingNewBed ? line_item.previous_bed_price_in_cents : 0,
    price_in_dollars: addingNewBed ? convertCost(centsToDollars(line_item.previous_bed_price_in_cents)) : "0",
  };
};

/**
 * Sets the product id to a price override based on the category of the line item
 * @param {Object} line_item - The line item to use.
 * @param {string} library_id - The ID (UUID) of the library that belongs to the line item.
 * @returns {string} The id of the product/line item
 */
export const getProductIdBasedOnLibrary = (line_item, library_id) => {
  if (library_id === LIBRARY_IDS.housing) {
    return line_item?.room?.id ?? line_item?.room_id;
  }
  return line_item.id;
};

/**
 * Checks if line item price (excursion, room, misc prod, etc) in the order matches the current pricing of the session
 * @param {Object} line_item - The line item to compare pricing.
 * @param {string} library_id - The ID (UUID) of the library that belongs to the line item.
 * @param {Array<Object>} session_prices - The list of prices of a library (room, excursions, misc products, etc) supplied by the session pricing endpoint.
 * @returns {undefined | Object} If prices match undefined is returned, if prices are different then it returns the line item with the latest price
 */
export const lineItemPriceHasChanged = (line_item, session_prices, library_id = "") => {
  if (Array.isArray(session_prices)) {
    const price_in_cents = getPriceBeforeFulfillment(line_item);
    const line_item_product_id = getProductIdBasedOnLibrary(line_item, library_id);

    return session_prices.find((sessionPricingItem) => {
      return (
        sessionPricingItem?.product_id &&
        line_item_product_id &&
        sessionPricingItem?.product_id === line_item_product_id &&
        price_in_cents !== sessionPricingItem?.total_price_in_cents
      );
    });
  }
  return;
};

/**
 * Tells you if the line item has a pending price override.
 * @param {Object} line_item - The line item to check.
 * @returns {Boolean}
 */
export const lineItemHasPendingOverride = (line_item) => {
  return line_item.latest_unapproved_price_override?.status === PriceOverrideStatus.NeedsReview;
};

/**
 * Tells you if the line item has a canceled price override.
 * @param {Object} line_item - The line item to check.
 * @returns {Boolean}
 */
export const lineItemHasCanceledOverride = (line_item) => {
  return [PriceOverrideStatus.Canceled, PriceOverrideStatus.Rejected].includes(
    line_item.latest_unapproved_price_override?.status,
  );
};

/**
 * Tells you if the line item has an approved price override.
 * @param {Object} line_item - The line item to check.
 * @returns {Boolean}
 */
export const lineItemCurrentlyApproved = (line_item) => {
  return line_item.latest_approved_price_override?.status === PriceOverrideStatus.Approved;
};

/**
 * Determines which option will be shown by default in the price dropdown.
 * @param {Object} line_item - The line item to compare pricing.
 * @param {undefined | Object} newer_session_price - The line item with must up to date price (obtained from lineItemPriceHasChanged)
 * @returns {string} value to be selected by default in price dropdown
 */
export const setPriceOverrideDropdownOption = (line_item, newer_session_price) => {
  const hasApprovedPriceOverride = line_item?.latest_approved_price_override?.status === PriceOverrideStatus.Approved;
  const hasPendingPriceOverrides = lineItemHasPendingOverride(line_item);
  const line_item_total = getPriceBeforeFulfillment(line_item);
  const overridePriceMatchesOrder =
    line_item_total === line_item.latest_unapproved_price_override?.override_amount_in_cents;
  const sessionPriceDifferentThanOrder =
    Boolean(
      typeof newer_session_price?.total_price_in_cents === "number" &&
        !Number.isNaN(newer_session_price.total_price_in_cents),
    ) && line_item_total !== newer_session_price.total_price_in_cents;

  // Special scenarios
  const assignNewHousingBed = lineItemAboutToAssignBed(line_item);

  if (assignNewHousingBed.addingNewBed && line_item_total !== assignNewHousingBed.price_in_cents) {
    return OPTIONS_PRICE_OVERRIDE.KEEP_ORDER.value;
  } else if (hasPendingPriceOverrides && overridePriceMatchesOrder) {
    return OPTIONS_PRICE_OVERRIDE.KEEP_ORDER.value;
  } else if (hasPendingPriceOverrides && !overridePriceMatchesOrder) {
    return OPTIONS_PRICE_OVERRIDE.CUSTOM_PRICE.value;
  } else if (
    !hasPendingPriceOverrides &&
    sessionPriceDifferentThanOrder &&
    line_item.price_before_fulfillment_in_cents != null &&
    line_item.total_price_in_cents !== line_item.price_before_fulfillment_in_cents
  ) {
    return OPTIONS_PRICE_OVERRIDE.KEEP_CURRENT.value;
  } else if (!hasPendingPriceOverrides && sessionPriceDifferentThanOrder) {
    return OPTIONS_PRICE_OVERRIDE.KEEP_ORDER.value;
  } else if (!hasPendingPriceOverrides && hasApprovedPriceOverride) {
    return undefined; // No default, user must make a selection
  }
  return OPTIONS_PRICE_OVERRIDE.KEEP_CURRENT.value; // (Default) assumes there's no newer price for line item
};

/**
 * Builds which options must be available in the price dropdown.
 * @param {Object} line_item - The line item to compare pricing.
 * @param {undefined | Object} newer_session_price - The line item with must up to date price (obtained from lineItemPriceHasChanged)
 * @param {undefined | string} default_option - the default option selected
 * @returns {Array<Object>} The options available in the price dropdown
 */
export const generatePriceOverrideOptions = (line_item, newer_session_price, default_option = "") => {
  const options: Array<PriceOption> = [];
  const assignNewHousingBed = lineItemAboutToAssignBed(line_item);
  const line_item_total = getPriceBeforeFulfillment(line_item);
  const order_price = convertCost(centsToDollars(line_item_total));
  const sessionPriceDifferentThanOrder =
    Boolean(
      typeof newer_session_price?.total_price_in_cents === "number" &&
        !Number.isNaN(newer_session_price.total_price_in_cents),
    ) && line_item_total !== newer_session_price.total_price_in_cents;
  const hasApprovedPriceOverride = line_item?.latest_approved_price_override?.status === PriceOverrideStatus.Approved;

  if (sessionPriceDifferentThanOrder) {
    // Prices are different, add both options
    const newer_price = newer_session_price.total_price_in_cents
      ? `${convertCost(centsToDollars(newer_session_price.total_price_in_cents))}`
      : newer_session_price.product_price;
    options.push({
      ...OPTIONS_PRICE_OVERRIDE.KEEP_CURRENT,
      label: `Current Product Price (${newer_price})`,
      disabled: hasApprovedPriceOverride,
    });
    options.push({
      ...OPTIONS_PRICE_OVERRIDE.KEEP_ORDER,
      label: `Keep Order Price (${order_price})`,
    });
  } else {
    // Assumes current order price and product price are the same
    options.push({
      ...OPTIONS_PRICE_OVERRIDE.KEEP_CURRENT,
      label: `Current Product Price (${order_price})`,
      disabled: hasApprovedPriceOverride,
    });
  }

  const keepOrderInList = options.find((option) => option.value === OPTIONS_PRICE_OVERRIDE.KEEP_ORDER.value);
  if (
    default_option === OPTIONS_PRICE_OVERRIDE.KEEP_ORDER.value &&
    assignNewHousingBed.addingNewBed &&
    line_item_total !== assignNewHousingBed.price_in_cents &&
    !keepOrderInList
  ) {
    options.push({
      ...OPTIONS_PRICE_OVERRIDE.KEEP_ORDER,
      label: `Keep Order Price (${assignNewHousingBed.price_in_dollars})`,
    });
  } else if (default_option === OPTIONS_PRICE_OVERRIDE.KEEP_ORDER.value && !keepOrderInList) {
    options.push({ ...OPTIONS_PRICE_OVERRIDE.KEEP_ORDER, label: `Keep Order Price (${order_price})` });
  }

  options.push({ ...OPTIONS_PRICE_OVERRIDE.CUSTOM_PRICE });
  return options;
};

/**
 * Generates structure of price override to modify
 * @param {Object} line_item - The line item to use.
 * @param {string} price_selection - The option selected in price dropdown
 * @param {Object} pending_override - The current pending price override of the line item.
 * @param {string} library_id - The ID (UUID) of the library to create/modify the price override (room, excursion, misc prod, etc)
 * @param {string} order_id - The ID (UUID) of the order to modify.
 * @returns {Object} A new price override object or the existing one.
 */
export const generateDefaultPriceOverride = (
  line_item,
  price_selection: string = OPTIONS_PRICE_OVERRIDE.KEEP_ORDER.value,
  pending_override: PriceOverride,
  library_id: Libraries,
  order_id: string,
) => {
  const assignNewHousingBed = lineItemAboutToAssignBed(line_item);
  const priceToSet = assignNewHousingBed.addingNewBed
    ? assignNewHousingBed.price_in_cents
    : getPriceBeforeFulfillment(line_item);

  return pending_override?.id
    ? {
        ...pending_override,
        override_amount_in_dollars: centsToDollars(pending_override.override_amount_in_cents),
      } // Existing
    : {
        id: "",
        request_notes: `${price_selection === OPTIONS_PRICE_OVERRIDE.KEEP_ORDER.value ? "Keep Order Price" : ""}`,
        override_amount_in_dollars:
          price_selection === OPTIONS_PRICE_OVERRIDE.CUSTOM_PRICE.value ? 0 : centsToDollars(priceToSet),
        override_amount_in_cents: price_selection === OPTIONS_PRICE_OVERRIDE.CUSTOM_PRICE.value ? 0 : priceToSet,
        override_reason_id: "",
        order_id,
        product_id: getProductIdBasedOnLibrary(line_item, library_id),
        library_id,
      }; // New
};

export const getLatestPriceOverride = (line_item) => {
  // Both have dates
  if (
    line_item?.latest_approved_price_override?.created_at &&
    line_item?.latest_unapproved_price_override?.created_at
  ) {
    // Return by latest date
    const dates = [line_item.latest_approved_price_override, line_item.latest_unapproved_price_override];

    return dates.reduce((latest, current) => {
      return new Date(current.created_at).getTime() > new Date(latest.created_at).getTime() ? current : latest;
    });
  } else if (
    line_item?.latest_unapproved_price_override?.created_at &&
    !line_item?.latest_approved_price_override?.created_at
  ) {
    return line_item.latest_unapproved_price_override; // only unapproved has date
  } else if (
    line_item?.latest_approved_price_override?.created_at &&
    !line_item?.latest_unapproved_price_override?.created_at
  ) {
    // only approved has date
    return line_item.latest_approved_price_override;
  }

  // Default - latest unapproved
  return line_item.latest_unapproved_price_override;
};

/**
 * Generates object to use in template to display price override status and actions
 * @param {Object} line_item - The line item to use.
 * @returns {Object} An object used to easily display in template the price override status
 */
export const buildStatusRow = (line_item) => {
  // Get Latest Price Override
  const latest_price_override = getLatestPriceOverride(line_item);

  const display: boolean = [
    PriceOverrideStatus.Canceled,
    PriceOverrideStatus.Rejected,
    PriceOverrideStatus.Approved,
    PriceOverrideStatus.NeedsReview,
  ].includes(latest_price_override?.status);
  let labelStatus: string =
    latest_price_override?.status === PriceOverrideStatus.NeedsReview ? "Pending" : latest_price_override?.status;
  const revertLabel = "Restore original price";
  let labelColor = "";
  let icon = "";
  let displayRevert = false;

  // Approved price override state amount
  if (
    latest_price_override?.status === PriceOverrideStatus.Approved &&
    Number.isFinite(latest_price_override?.override_amount_in_cents)
  ) {
    labelStatus += ` for ${convertCost(centsToDollars(latest_price_override.override_amount_in_cents))}`;
  }

  if (latest_price_override?.status === PriceOverrideStatus.NeedsReview) {
    labelColor = "text-amber-300";
    icon = "rate_review";
  } else if ([PriceOverrideStatus.Canceled, PriceOverrideStatus.Rejected].includes(latest_price_override?.status)) {
    labelColor = "text-red-700";
    icon = "unpublished";
  } else if (latest_price_override?.status === PriceOverrideStatus.Approved) {
    labelColor = "text-green-500";
    icon = "task_alt";
    displayRevert = latest_price_override.override_reason_id !== REVERTED_OVERRIDE_REASON_ID;
  }

  return {
    display,
    labelStatus,
    labelColor,
    icon,
    displayRevert,
    revertLabel,
  };
};
